import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export const ReviewsTab = () => {
  const [data, setData] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [form, setForm] = useState({ author: "", apartment_slug: "", rating: 5, text: "" });

  const load = () => api.get("/admin/reviews").then((r) => setData(r.data)).catch(() => setData({ published: false, reviews: [] }));
  useEffect(() => {
    load();
    api.get("/apartments").then((r) => {
      setApartments(r.data);
      if (r.data.length) setForm((f) => ({ ...f, apartment_slug: f.apartment_slug || r.data[0].slug }));
    }).catch(() => {});
  }, []);

  const togglePublish = async () => {
    try {
      const { data: res } = await api.post("/admin/reviews/publish", { published: !data.published });
      toast.success(res.published ? "Sección de reseñas PUBLICADA en la web" : "Sección de reseñas OCULTA");
      load();
    } catch {
      toast.error("No se pudo cambiar la publicación");
    }
  };

  const add = async () => {
    try {
      await api.post("/admin/reviews", { ...form, rating: Number(form.rating) });
      toast.success("Reseña añadida");
      setForm((f) => ({ ...f, author: "", text: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo añadir la reseña");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Reseña eliminada");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  if (!data) return <p className="text-[#A0A3A9] text-sm p-6">Cargando reseñas…</p>;

  return (
    <div data-testid="reviews-tab" className="flex flex-col gap-6">
      <div className={`border p-5 flex flex-wrap items-center justify-between gap-4 ${data.published ? "bg-[#4A6B53]/15 border-[#4A6B53]/50" : "bg-[#181A1F] border-[#2A2D35]"}`}>
        <div>
          <p className="text-sm text-[#FDFBF7] font-medium">
            {data.published ? "La sección de reseñas está PUBLICADA en la web" : "La sección de reseñas está OCULTA"}
          </p>
          <p className="text-xs text-[#A0A3A9] mt-1">
            {data.published ? "Los visitantes la ven en la página principal." : "Nadie la ve en la web hasta que la publiques. Las marcadas como «ejemplo» son de muestra: sustitúyelas por las tuyas."}
          </p>
        </div>
        <button
          data-testid="reviews-publish-toggle"
          onClick={togglePublish}
          className={`flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors ${data.published ? "border border-[#2A2D35] text-[#A0A3A9] hover:text-[#FDFBF7]" : "bg-[#D5C0A1] text-[#0F1115] hover:bg-[#FDFBF7]"}`}
        >
          {data.published ? <><EyeSlash size={15} /> Ocultar</> : <><Eye size={15} /> Publicar en la web</>}
        </button>
      </div>

      <div className="bg-[#181A1F] border border-[#2A2D35] p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-4">Añadir reseña</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Autor</label>
            <input data-testid="review-author" className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5 min-w-[160px]" placeholder="María G." value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Apartamento</label>
            <select data-testid="review-apartment" value={form.apartment_slug} onChange={(e) => setForm({ ...form, apartment_slug: e.target.value })} className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5 min-w-[170px]">
              {apartments.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Estrellas</label>
            <select data-testid="review-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Texto</label>
            <input data-testid="review-text" className="w-full bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5" placeholder="Lo que contó el huésped…" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
          </div>
          <button data-testid="review-add" onClick={add} disabled={!form.author.trim() || !form.text.trim()} className="px-5 py-2.5 bg-[#D5C0A1] text-[#0F1115] text-xs uppercase tracking-[0.15em] hover:bg-[#FDFBF7] transition-colors disabled:opacity-40">
            Añadir
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {data.reviews.length === 0 && <p className="text-[#A0A3A9] text-sm p-6 bg-[#181A1F] border border-[#2A2D35]">Aún no hay reseñas.</p>}
        {data.reviews.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id.slice(0, 8)}`} className="bg-[#181A1F] border border-[#2A2D35] p-4 flex items-start justify-between gap-4">
            <div className="text-sm text-[#FDFBF7]/85">
              <p className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{r.author}</span>
                <span className="flex gap-0.5 text-[#D5C0A1]">{Array.from({ length: r.rating }).map((_, s) => <Star key={s} size={12} weight="fill" />)}</span>
                <span className="text-xs text-[#A0A3A9]">· {r.apartment_name}</span>
                {r.sample && <span className="text-[10px] uppercase tracking-[0.15em] text-[#C8A97E] border border-[#C8A97E]/40 px-2 py-0.5">ejemplo</span>}
              </p>
              <p className="text-[#A0A3A9] mt-1.5">{r.text}</p>
            </div>
            <button data-testid={`review-delete-${r.id.slice(0, 8)}`} onClick={() => remove(r.id)} className="p-2 border border-[#2A2D35] text-[#E08A6C] hover:border-[#E08A6C] transition-colors shrink-0" aria-label="Eliminar reseña">
              <Trash size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
